import { Organization } from "@common/types/organization";
import {
    EntityProfileIndustry,
    EntityProfileRevenueBracket,
    EntityProfileType,
} from "@common/types/entity";
import {
    BudgetType,
    MeetingOption,
    OrganizationStageOfBusiness,
    ProjectTimeline,
} from "@common/schemas/organization.schema";
import { FC } from "react";
import { Form, FormikProvider, useFormik } from "formik";
import { z } from "zod";
import { submitHelper, validateFormikWithZodSchema } from "@helpers/form";
import { createFormikFields } from "@components/forms/fields/createFormikFields";
import type { SelectFieldOption } from "@components/forms/fields/SelectField";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { useUpdateOrganizationProfileMutation } from "@api/organization.api";
import {
    budgetTypeLabels,
    entityProfileTypeLabels,
    industryLabels,
    meetingOptionLabels,
    projectTimelineLabels,
    revenueBracketLabels,
    stageOfBusinessLabels,
} from "@components/entity/constants";
import { HStack } from "@design-system/HStack/HStack";
import { VStack } from "@design-system/VStack/VStack";
import { MultiSelectRow } from "./MultiSelectRow";
import { buildProfilePayload } from "./buildProfilePayload";
import styles from "./SettingsPage.module.scss";

const entityTypeItems = Object.values(EntityProfileType).map((v) => ({
    value: v,
    label: entityProfileTypeLabels[v],
}));

const stageItems = Object.values(OrganizationStageOfBusiness).map((v) => ({
    value: v,
    label: stageOfBusinessLabels[v],
}));

const revenueBracketItems = Object.values(EntityProfileRevenueBracket).map(
    (v) => ({ value: v, label: revenueBracketLabels[v] }),
);

const industryItems = Object.values(EntityProfileIndustry).map((v) => ({
    value: v,
    label: industryLabels[v],
}));

const projectTimelineItems = Object.values(ProjectTimeline).map((v) => ({
    value: v,
    label: projectTimelineLabels[v],
}));

const budgetItems = Object.values(BudgetType).map((v) => ({
    value: v,
    label: budgetTypeLabels[v],
}));

const meetingOptionItems = Object.values(MeetingOption).map((v) => ({
    value: v,
    label: meetingOptionLabels[v],
}));

const multiEntityChoiceSchema = z.enum(["yes", "no"]);

type MultiEntityChoice = z.infer<typeof multiEntityChoiceSchema>;

const schema = z.object({
    supportedEntityTypes: z.array(z.nativeEnum(EntityProfileType)),
    multiEntitySupport: multiEntityChoiceSchema.optional(),
    stagesOfBusiness: z.array(z.nativeEnum(OrganizationStageOfBusiness)),
    revenueBrackets: z.array(z.nativeEnum(EntityProfileRevenueBracket)),
    industries: z.array(z.nativeEnum(EntityProfileIndustry)),
    projectTimelines: z.array(z.nativeEnum(ProjectTimeline)),
    budgets: z.array(z.nativeEnum(BudgetType)),
    meetingOptions: z.array(z.nativeEnum(MeetingOption)),
});

type FormValues = z.infer<typeof schema>;

const fields = createFormikFields<FormValues>();

const multiEntityOptions: ReadonlyArray<SelectFieldOption<MultiEntityChoice>> =
    [
        { value: "yes", label: "Yes" },
        { value: "no", label: "No" },
    ];

function toMultiEntityChoice(
    isSupported: boolean | undefined,
): MultiEntityChoice | undefined {
    if (isSupported === undefined) {
        return undefined;
    }
    return isSupported ? "yes" : "no";
}

function fromMultiEntityChoice(
    choice: MultiEntityChoice | undefined,
): boolean | undefined {
    if (choice === undefined) {
        return undefined;
    }
    return choice === "yes";
}

interface Props {
    organization: Organization;
    onClose(): void;
}

export const EditMatchingModal: FC<Props> = ({ organization, onClose }) => {
    const { toast, error } = useToaster();
    const updateProfile = useUpdateOrganizationProfileMutation();
    const profile = organization.profile;

    const formik = useFormik<FormValues>({
        initialValues: {
            supportedEntityTypes: profile?.supportedEntityTypes ?? [],
            multiEntitySupport: toMultiEntityChoice(
                profile?.multiEntitySupport ?? undefined,
            ),
            stagesOfBusiness: profile?.stagesOfBusiness ?? [],
            revenueBrackets: profile?.revenueBrackets ?? [],
            industries: profile?.industries ?? [],
            projectTimelines: profile?.projectTimelines ?? [],
            budgets: profile?.budgets ?? [],
            meetingOptions: profile?.meetingOptions ?? [],
        },
        validate: validateFormikWithZodSchema(schema),
        onSubmit: submitHelper({
            handler: async (values) => {
                await updateProfile.mutateAsync({
                    organizationId: organization.id,
                    payload: buildProfilePayload(profile, {
                        ...values,
                        multiEntitySupport: fromMultiEntityChoice(
                            values.multiEntitySupport,
                        ),
                    }),
                });
                toast("Matching preferences updated");
                onClose();
            },
            failureHandler: async (err) => {
                // eslint-disable-next-line no-console
                console.error("Failed to save matching preferences", err);
                error("Failed to save matching preferences");
            },
        }),
    });

    const { values, setFieldValue, setFieldTouched } = formik;

    return (
        <FormikProvider value={formik}>
            <VStack as={Form} gap={16} className={styles.settingForm}>
                <MultiSelectRow
                    name="supportedEntityTypes"
                    label="Supported entity types"
                    items={entityTypeItems}
                    value={values.supportedEntityTypes}
                    onChange={(v) => {
                        void setFieldValue("supportedEntityTypes", v);
                    }}
                    onBlur={() => {
                        void setFieldTouched("supportedEntityTypes", true);
                    }}
                />

                <fields.Select
                    name="multiEntitySupport"
                    label="Multi-entity support"
                    options={multiEntityOptions}
                    placeholder="Select"
                    buttonProps={{ size: "xl", block: true }}
                />

                <MultiSelectRow
                    name="stagesOfBusiness"
                    label="Stages of business"
                    items={stageItems}
                    value={values.stagesOfBusiness}
                    onChange={(v) => {
                        void setFieldValue("stagesOfBusiness", v);
                    }}
                    onBlur={() => {
                        void setFieldTouched("stagesOfBusiness", true);
                    }}
                />

                <MultiSelectRow
                    name="revenueBrackets"
                    label="Revenue brackets"
                    items={revenueBracketItems}
                    value={values.revenueBrackets}
                    onChange={(v) => {
                        void setFieldValue("revenueBrackets", v);
                    }}
                    onBlur={() => {
                        void setFieldTouched("revenueBrackets", true);
                    }}
                />

                <MultiSelectRow
                    name="industries"
                    label="Industries"
                    items={industryItems}
                    value={values.industries}
                    onChange={(v) => {
                        void setFieldValue("industries", v);
                    }}
                    onBlur={() => {
                        void setFieldTouched("industries", true);
                    }}
                />

                <MultiSelectRow
                    name="projectTimelines"
                    label="Project timelines"
                    items={projectTimelineItems}
                    value={values.projectTimelines}
                    onChange={(v) => {
                        void setFieldValue("projectTimelines", v);
                    }}
                    onBlur={() => {
                        void setFieldTouched("projectTimelines", true);
                    }}
                />

                <MultiSelectRow
                    name="budgets"
                    label="Budgets"
                    items={budgetItems}
                    value={values.budgets}
                    onChange={(v) => {
                        void setFieldValue("budgets", v);
                    }}
                    onBlur={() => {
                        void setFieldTouched("budgets", true);
                    }}
                />

                <MultiSelectRow
                    name="meetingOptions"
                    label="Meeting options"
                    items={meetingOptionItems}
                    value={values.meetingOptions}
                    onChange={(v) => {
                        void setFieldValue("meetingOptions", v);
                    }}
                    onBlur={() => {
                        void setFieldTouched("meetingOptions", true);
                    }}
                />

                <HStack as="footer" justify="end">
                    <ButtonWithLoader
                        size="xl"
                        loading={formik.isSubmitting}
                        type="submit"
                        variant="primary"
                    >
                        Save
                    </ButtonWithLoader>
                </HStack>
            </VStack>
        </FormikProvider>
    );
};
